import { AsyncPipe, NgClass, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, ChildActivationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { GeneralQuery } from '../../client/state';
import { SparrowIconEnum } from '../../core/icons-registry';
import { LangToIcon, SupportedLanguagesType } from '../../core/localization/localization';
import { changeLanguage } from '../../core/utils';
import { preventDefault } from '../../shared/decorators/prevent-default.decorator';
import { stopPropagation } from '../../shared/decorators/stop-propagation.decorator';
import { LocLinkInfo, SiteLinkInfo } from '../client-navbar/client-navbar.component';

@Component({
  selector: 'sparrow-client-header',
  templateUrl: './client-header.component.html',
  styleUrls: ['./client-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatMenuModule, NgClass, RouterLinkActive, RouterLink, AsyncPipe, UpperCasePipe],
})
export class ClientHeaderComponent implements OnInit, OnDestroy {
  selectedLanAbbr: string;
  selectedLanLabel: string;
  langDropdown = false;
  mobileIsOpen = false;
  siteLinks: SiteLinkInfo[];
  localizationLinks: LocLinkInfo[] = [
    {
      lang: 'en',
      icon: LangToIcon.en,
      langLabel: 'EN',
      label: 'English',
    },
    {
      lang: 'de',
      icon: LangToIcon.de,
      langLabel: 'DE',
      label: 'Deutsch',
    },
  ];
  hideHeader$: Observable<boolean>;
  readonly SparrowIcon = SparrowIconEnum;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private generalQuery: GeneralQuery,
    private changeDetectorRef: ChangeDetectorRef,
    // `LOCALE_ID` is actually string, but should always conform to our list (except for local development where it is 'en-us')
    @Inject(LOCALE_ID) private locale: SupportedLanguagesType,
  ) {
    this.selectedLanAbbr = this.locale;
    this.selectedLanLabel = this.localizationLinks.find((val) => val.lang === locale).label;
  }

  ngOnInit() {
    this.hideHeader$ = this.router.events.pipe(
      filter((event) => event instanceof ChildActivationEnd),
      startWith(false),
      map(() => {
        const leafNode = this.getLeafNode(this.activatedRoute);
        return leafNode.snapshot.data.hideHeader ?? false;
      }),
      takeUntil(this.unsubscribe$),
    );

    combineLatest([this.generalQuery.hasSmAccess$, this.generalQuery.hasCaAccess$, this.generalQuery.hasPlanAccess$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([smAccess, caAccess, planAccess]: [boolean, boolean, boolean]) => {
        this.siteLinks = [
          {
            url: '/data-cleaning',
            label: $localize`:@@cleanNavLabel:Clean`,
            icon: 'data-cleaning',
            isVisible: caAccess,
          },
          {
            url: '/planning-tool',
            label: $localize`:@@planNavLabel:Plan`,
            icon: 'stock-planning',
            isVisible: planAccess,
          },
          {
            url: '/stock-management',
            label: $localize`:@@stockManagementNavLabel:Stock Management`,
            icon: 'stock-management',
            isVisible: smAccess,
          },
        ];
        this.changeDetectorRef.markForCheck();
      });
  }

  changeLanguage(lang: SupportedLanguagesType) {
    changeLanguage(lang);
  }

  logout() {
    this.authService.logout();
  }

  @stopPropagation()
  @preventDefault()
  onLangToggle($event: MouseEvent) {
    this.langDropdown = !this.langDropdown;
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getLeafNode(tree: ActivatedRoute): ActivatedRoute {
    return !!tree.firstChild ? this.getLeafNode(tree.firstChild) : tree;
  }
}
